import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardService } from "../../services/DashboardService";
import { logger } from "../../utils/logger";

// Query Keys
export const dashboardKeys = {
  all: ["dashboards"],
  lists: () => [...dashboardKeys.all, "list"],
  list: (orgId) => [...dashboardKeys.lists(), orgId],
  details: () => [...dashboardKeys.all, "detail"],
  detail: (id) => [...dashboardKeys.details(), id],
};

/**
 * Hook to fetch dashboards for an organization
 * @param {string} orgId - Organization ID
 * @returns {object} Query result with dashboards data
 */
export function useDashboards(orgId) {
  return useQuery({
    queryKey: dashboardKeys.list(orgId || ""),
    queryFn: async () => {
      if (!orgId) {
        return [];
      }
      try {
        const response = await DashboardService.fetchDashboards(orgId);
        return response || [];
      } catch (error) {
        logger.error("Error fetching dashboards:", error);
        throw error; // Let React Query handle the error
      }
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useShareDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId) => DashboardService.shareDashboard(dashboardId),
    onSuccess: (_, dashboardId) => {
      // Invalidate the specific dashboard to refresh sharing status
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.detail(dashboardId),
      });
      // Also invalidate the list to refresh any summary data
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, description, devices, organisation_id }) =>
      DashboardService.createDashboard(
        name,
        description,
        devices,
        organisation_id
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.list(variables.organisation_id),
      });
    },
  });
}

export function useUpdateDashboardMeta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dashboardId, data }) =>
      DashboardService.editDashboard(dashboardId, data),
    onSuccess: (_, { dashboardId, data }) => {
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.detail(dashboardId),
      });

      if (data?.organisationId) {
        queryClient.invalidateQueries({
          queryKey: dashboardKeys.list(data.organisationId),
        });
      } else {
        queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
      }
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dashboardId }) =>
      DashboardService.deleteDashboard(dashboardId),
    onSuccess: (_, { dashboardId, organisationId }) => {
      queryClient.removeQueries({
        queryKey: dashboardKeys.detail(dashboardId),
      });

      if (organisationId) {
        queryClient.invalidateQueries({
          queryKey: dashboardKeys.list(organisationId),
        });
      } else {
        queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
      }
    },
  });
}
